import React, { useEffect, useMemo, useState } from "react";
import { getAuth } from "firebase/auth";
import { addDoc, collection, doc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";

import SettingsCard from "./cards/SettingsCard";
import FamilyCard from "./cards/FamilyCard";
import IntegrationsCard from "./cards/IntegrationsCard";
import PresetTimeCard from "./cards/PresetTimeCard";
import TaskTemplatesCard from "./cards/TaskTemplatesCard";
import MarketplaceCard from "./cards/MarketplaceCard";
import AIRoutinesCard from "./cards/AIRoutinesCard";

export default function SettingsDashboard({ familyId, familySettings, families = [], activeFamilyId, setActiveFamilyId, canManage = false }) {
  const auth = getAuth();
  const isPro = familySettings?.isPro || false;
  const [newFamilyName, setNewFamilyName] = useState("");
  const visibilityMap = familySettings?.dashboardCardVisibility || {};

  const sections = useMemo(
    () => [
      { id: "layout", label: "Parent Hub (Daily Flow)" },
      { id: "settings", label: "App Settings" },
      { id: "tools", label: "Tools & Library" },
      { id: "family", label: "Family & Roles" },
      { id: "integrations", label: "Calendar Sync" }
    ],
    []
  );
  const [activeSection, setActiveSection] = useState("layout");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (event) => {
      const next = event?.detail;
      if (next && sections.some(section => section.id === next)) {
        setActiveSection(next);
      }
    };
    window.addEventListener("tour:setSettingsSection", handler);
    return () => window.removeEventListener("tour:setSettingsSection", handler);
  }, [sections]);

  const cardOptions = [
    { id: "tasks", label: "Assigned Chores" },
    { id: "approvals", label: "Approvals" },
    { id: "clarifications", label: "Clarifications" },
    { id: "repeats", label: "Repeating Chores" },
    { id: "weeklyRecap", label: "Weekly Recap", requiresPro: true },
    { id: "calendar", label: "Calendar" },
    { id: "bank", label: "Bank", requiresPro: true },
    { id: "rewards", label: "Rewards", requiresPro: true },
    { id: "goals", label: "Family Goals", requiresPro: true },
    { id: "analytics", label: "Analytics", requiresPro: true },
    { id: "settingsOverview", label: "Settings Shortcut" }
  ];

  const toggleCardVisibility = async (cardId) => {
    if (!canManage) return;
    const isVisible = visibilityMap[cardId] !== false;
    const next = { ...visibilityMap, [cardId]: !isVisible };
    await updateDoc(doc(db, "families", familyId), {
      dashboardCardVisibility: next
    });
  };

  const createFamily = async () => {
    if (!canManage || !newFamilyName) return;
    const timezone = typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC";
    const docRef = await addDoc(collection(db, "families"), {
      pin: "1234",
      theme: "ocean",
      isPro: false,
      paidPro: false,
      complimentaryPro: false,
      subscriptionStatus: null,
      subscriptionPlan: null,
      subscriptionCancelAtPeriodEnd: false,
      subscriptionRenewAt: null,
      subscriptionExpiresAt: null,
      stripeCustomerId: null,
      stripeSubscriptionId: null,
      ownerUid: auth.currentUser.uid,
      familyName: newFamilyName,
      memberUids: [auth.currentUser.uid],
      timezone
    });
    await setDoc(doc(db, "families", docRef.id, "members", auth.currentUser.uid), {
      role: "parent",
      displayName: auth.currentUser.displayName || auth.currentUser.email?.split("@")[0] || "Parent",
      joinedAt: new Date()
    });
    setNewFamilyName("");
    if (setActiveFamilyId) setActiveFamilyId(docRef.id);
  };

  return (
    <div className="settings-page settings-page-layout">
      <div className="settings-sidebar">
        {sections.map(section => (
          <button
            key={section.id}
            className={`btn-nav ${activeSection === section.id ? "active-settings-tab" : ""}`}
            onClick={() => setActiveSection(section.id)}
            data-tour={
              section.id === "settings"
                ? "settings-section-settings"
                : section.id === "family"
                  ? "settings-section-family"
                  : section.id === "integrations"
                    ? "settings-section-integrations"
                    : undefined
            }
          >
            {section.label}
          </button>
        ))}
      </div>
      <div className="settings-content">
        {activeSection === "layout" && (
          <div className="settings-section">
            <h2>Daily Flow Layout</h2>
            <p style={{ opacity: 0.7, marginTop: "-6px" }}>
              Drag cards directly in the Parent Hub to reorder, and use the resize handle to size them.
              The default order is Assign → Approvals → Clarifications unless you move them.
            </p>
            {!canManage && (
              <p style={{ textAlign: "center", opacity: 0.6 }}>You have view-only access.</p>
            )}
            {canManage && (
              <div className="glass parent-card" style={{ height: "auto" }}>
                <div className="card-body">
                  <p style={{ margin: 0 }}>
                    Go to <strong>Parent Hub</strong> to drag and resize cards. Your layout saves automatically.
                  </p>
                </div>
              </div>
            )}
            <div style={{ marginTop: "18px" }}>
              <h3 style={{ marginBottom: "10px" }}>Show cards</h3>
              <div className="settings-grid">
                {cardOptions.map((option) => {
                  const isVisible = visibilityMap[option.id] !== false;
                  const locked = option.requiresPro && !isPro;
                  return (
                    <label
                      key={option.id}
                      className="settings-toggle-row"
                      style={{ opacity: locked ? 0.6 : 1 }}
                    >
                      <span>{option.label}{locked ? " (Pro)" : ""}</span>
                      <span className="settings-toggle">
                        <input
                          type="checkbox"
                          checked={isVisible && !locked}
                          onChange={() => toggleCardVisibility(option.id)}
                          disabled={!canManage || locked}
                        />
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeSection === "settings" && (
          <div className="settings-section settings-full">
            <SettingsCard familyId={familyId} familySettings={familySettings} canManage={canManage} />
          </div>
        )}

        {activeSection === "tools" && (
          <div className="settings-section">
            <h2>Tools & Library</h2>
            <p style={{ opacity: 0.7, marginTop: "-6px" }}>
              Setup tools and templates live here so the Parent Hub stays simple.
            </p>
            {!canManage && (
              <p style={{ textAlign: "center", opacity: 0.6 }}>You have view-only access.</p>
            )}
            {canManage && (
              <div className="parent-grid">
                <PresetTimeCard familyId={familyId} familySettings={familySettings} canManage={canManage} />
                {isPro && (
                  <TaskTemplatesCard
                    familyId={familyId}
                    familySettings={familySettings}
                    isPro={familySettings.isPro}
                    canManage={canManage}
                  />
                )}
                {isPro && (
                  <MarketplaceCard familyId={familyId} familySettings={familySettings} canManage={canManage} />
                )}
                {isPro && (
                  <AIRoutinesCard familyId={familyId} familySettings={familySettings} canManage={canManage} />
                )}
                {!isPro && (
                  <div className="glass parent-card">
                    <div className="header-flex">
                      <h2>Pro Tools</h2>
                    </div>
                    <div className="card-body">
                      <p style={{ opacity: 0.7 }}>
                        Templates, marketplace packs, and AI routines are Pro-only. Upgrade to unlock them.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeSection === "family" && (
          <div className="settings-section">
            <h2>Families</h2>
            {families.length > 0 && (
              <div style={{ display: "grid", gap: "8px", marginBottom: "16px" }}>
                {families.map(family => (
                  <button
                    key={family.id}
                    className={`btn-nav ${family.id === activeFamilyId ? "active-settings-tab" : ""}`}
                    onClick={() => setActiveFamilyId && setActiveFamilyId(family.id)}
                  >
                    {family.familyName || "Family"}
                  </button>
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: "8px" }}>
              <input
                placeholder="New family name"
                value={newFamilyName}
                data-tour="settings-add-family-input"
                onChange={(e) => setNewFamilyName(e.target.value)}
              />
              <button className="btn-nav success-btn" onClick={createFamily} data-tour="settings-add-family-button">
                Add Family
              </button>
            </div>
            <div style={{ marginTop: "16px" }}>
              <FamilyCard familyId={familyId} familySettings={familySettings} canManage={canManage} />
            </div>
          </div>
        )}

        {activeSection === "integrations" && isPro && (
          <IntegrationsCard familyId={familyId} familySettings={familySettings} canManage={canManage} />
        )}
        {activeSection === "integrations" && !isPro && (
          <div className="settings-section">
            <h2>Calendar Sync</h2>
            <p style={{ opacity: 0.7 }}>
              Calendar sync is Pro-only. Upgrade the family to enable import/export sync.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
